import {
  Button,
  Col,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  TablePaginationConfig,
} from "antd";
import { useEffect, useState } from "react";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { RootState } from "../../app/store";
import { AdminQrDTO } from "../../shared/dtos/adminQrDTO";
import {
  adminQrsAsync,
  updateAdminQrsValue,
} from "../../features/admin/adminQrsSlice";
import dayjs from "dayjs";
import { deleteAdminQr } from "../../services/AdminQrApi";
import AirportQRForm from "./form";
import ContainerLayout from "../common/containerLayout";
import { PlusCircleTwoTone } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}
const { Column } = Table;

const AirportQRs = () => {
  const dispatch = useAppDispatch();
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [airportQr, setAirportQr] = useState<AdminQrDTO | null>(null);
  const [data, setData] = useState(new Array<AdminQrDTO>());
  const loading = useAppSelector((state: RootState) => state.adminQrs.loading);

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {},
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    dispatch(updateAdminQrsValue());
  }, [dispatch]);

  const fetchData = () => {
    dispatch(
      adminQrsAsync({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
      }),
    ).then(({ payload }: any) => {
      setData(payload.adminQrDtos);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: payload.total,
        },
      });
    });

    setIgnoreFetch(true);
  };

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreFetch, JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AdminQrDTO> | SorterResult<AdminQrDTO>[],
  ) => {
    setIgnoreFetch(false);
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const deleteQRCode = async (qrId: number) => {
    try {
      await deleteAdminQr(qrId);
      message.success("El QR se ha eliminado satisfactoriamente.");
    } catch (error) {
      message.error("Ha ocurrido un error al imntentar eliminar el QR.");
    } finally {
      setIgnoreFetch(false);
    }
  };

  const handleUpsert = (shouldFetchData = false) => {
    setAirportQr(null);
    setOpenModal(false);

    if (shouldFetchData) setIgnoreFetch(false);
  };

  const handleOpenModal = (airportQr: AdminQrDTO) => {
    setAirportQr(
      data.find((airport: AdminQrDTO) => airport.id === airportQr.id)!,
    );
    setOpenModal(true);
  };

  return (
    <ContainerLayout title="QRs" showNotification={false}>
      <Col span={24}>
        <Button
          type="primary"
          icon={<PlusCircleTwoTone />}
          onClick={() => setOpenModal(true)}
        >
          QR
        </Button>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          style={{ minWidth: "100%" }}
          rowKey={(record: AdminQrDTO) => Number(record.id)}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        >
          <Column title="Id" dataIndex="id" width={100} />
          <Column title="Dirección" width={100} dataIndex="address" />
          <Column title="Tarifa (%)" width={40} dataIndex="fee" />
          <Column
            title="Expiración"
            dataIndex="expiration"
            width={40}
            render={(expiration) =>
              expiration && new Date(expiration).toString() !== "Invalid Date"
                ? dayjs(expiration).format("DD-MM-YYYY HH:mm")
                : ""
            }
          />
          <Column
            width={20}
            render={(record: AdminQrDTO) => {
              return (
                <Space direction="horizontal">
                  <Button
                    color="green"
                    variant="solid"
                    onClick={() => handleOpenModal(record)}
                  >
                    Visualizar
                  </Button>
                  <Popconfirm
                    title="Estas seguro de eliminar este qr?"
                    onConfirm={async () => {
                      await deleteQRCode(record.id!);
                    }}
                    okText="Si"
                    cancelText="No"
                    placement="bottom"
                  >
                    <Button color="danger" variant="solid">
                      Eliminar
                    </Button>
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
        <Modal
          title="Generar código QR de aeropuerto"
          open={openModal}
          onCancel={() => handleUpsert(false)}
          footer={null}
          width={600}
        >
          <AirportQRForm airportQr={airportQr} update={handleUpsert} />
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default AirportQRs;
